import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  supabase,
  SHARED_SUPABASE_URL,
  SHARED_SUPABASE_PUBLISHABLE_KEY,
} from '@/integrations/supabase/shared';

type State = 'loading' | 'valid' | 'already' | 'invalid' | 'confirming' | 'done' | 'error';

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>('loading');

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      try {
        const res = await fetch(
          `${SHARED_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SHARED_SUPABASE_PUBLISHABLE_KEY } }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { setState('invalid'); return; }
        if (data?.valid === false && data?.reason === 'already_unsubscribed') {
          setState('already'); return;
        }
        if (data?.valid === true) { setState('valid'); return; }
        setState('invalid');
      } catch {
        setState('error');
      }
    })();
  }, [token]);

  const confirm = async () => {
    setState('confirming');
    try {
      const { data, error } = await supabase.functions.invoke('handle-email-unsubscribe', {
        body: { token },
      });
      if (error) throw error;
      const d = data as any;
      if (d?.reason === 'already_unsubscribed') { setState('already'); return; }
      if (d?.success === true) { setState('done'); return; }
      if (d?.success === false) { setState('invalid'); return; }
      setState('done');
    } catch {
      setState('error');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'grid', placeItems: 'center',
      background: '#F8F4EC', padding: 24, fontFamily: "Barlow, -apple-system, sans-serif",
    }}>
      <div style={{
        maxWidth: 460, width: '100%', background: '#fff', borderRadius: 14,
        padding: '36px 32px', border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 20px 50px -20px rgba(0,0,0,0.15)',
      }}>
        <div style={{
          fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: '#C4823A', fontWeight: 600, marginBottom: 6,
        }}>HireEva.co</div>
        <h1 style={{
          fontFamily: "'Instrument Serif', Georgia, serif", fontStyle: 'italic',
          fontSize: 30, color: '#1a1a1a', margin: '0 0 16px', lineHeight: 1.1,
        }}>
          {state === 'done' || state === 'already' ? "You're unsubscribed." : 'Unsubscribe from Eva emails'}
        </h1>
        {state === 'loading' && <p style={text}>Checking your link…</p>}
        {state === 'invalid' && <p style={text}>This unsubscribe link is invalid or has expired.</p>}
        {state === 'error' && <p style={text}>Something went wrong. Please try again in a moment.</p>}
        {state === 'already' && <p style={text}>This email is already opted out — no further action needed.</p>}
        {state === 'done' && <p style={text}>Got it. We've removed your email from our list.</p>}
        {(state === 'valid' || state === 'confirming') && (
          <>
            <p style={text}>Click below to confirm and we'll stop emailing you immediately.</p>
            <button
              onClick={confirm}
              disabled={state === 'confirming'}
              style={{
                marginTop: 8, padding: '13px 22px', borderRadius: 999, border: 0,
                background: '#1a1a1a', color: '#fff', fontSize: 14, fontWeight: 600,
                cursor: state === 'confirming' ? 'default' : 'pointer',
                opacity: state === 'confirming' ? 0.6 : 1,
              }}
            >
              {state === 'confirming' ? 'Unsubscribing…' : 'Confirm unsubscribe'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const text = {
  fontSize: 15, color: '#3c3530', lineHeight: 1.6, margin: '0 0 8px',
};